import { useState, useMemo, useCallback } from 'react';
import { Container, TextField, Button, CircularProgress, Typography, Snackbar, Alert } from '@mui/material';
import { FaCheckCircle, FaPlusCircle } from 'react-icons/fa';
import { compact, values } from 'lodash-es';
import axios from 'axios';

function VerifyConsent() {
	const [consentSignature, setConsentSignature] = useState('');
	const [referenceId, setReferenceId] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [verifyResponse, setVerifyResponse] = useState(null);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState('info');
	console.log('DataLayer:', window.dataLayer);
	const [inputErrorContext, setInputErrorContext] = useState({
		signature: '',
		referenceId: '',
	});

	const isValid = useMemo(() => {
		const newErrorContext = {
			signature: '',
			referenceId: '',
		};

		if (!consentSignature.trim()) {
			newErrorContext.signature = 'Consent Signature is required.';
		}
		if (!referenceId.trim()) {
			newErrorContext.referenceId = 'Consent Reference ID is required.';
		}

		setInputErrorContext(newErrorContext);
		return compact(values(newErrorContext)).length === 0;
	}, [consentSignature, referenceId]);

	const verifyConsent = useCallback(async () => {
		if (!isValid) return;

		setSubmitting(true);
		setVerifyResponse(null);
		setSnackbarOpen(false);

		try {
			const CONSENT_URI_FOR = sessionStorage.getItem('apiUrl');

			const verifyConsentCall = async (reference_id, signature) => {
				const uri = CONSENT_URI_FOR.replace('collect', 'verify');
				return axios.post(uri, { signature, reference_id }).then((res) => res.data);
			};

			const res = await verifyConsentCall(referenceId, consentSignature);

			setVerifyResponse(res);
			setSnackbarMessage('Verification ' + (res.is_verified ? 'Successful' : 'Failed'));
			setSnackbarSeverity(res.is_verified ? 'success' : 'error');
			setSnackbarOpen(true);
		} catch (err) {
			console.error('Verification Error:', err);
			setSnackbarMessage('Error: ' + err.message);
			setSnackbarSeverity('error');
			setSnackbarOpen(true);
		} finally {
			setSubmitting(false);
		}
	}, [consentSignature, isValid, referenceId]);

	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setSnackbarOpen(false);
	};

	return (
		<section
			className="hero min-h-screen flex flex-col items-center bg-cover bg-center bg-fixed"
			style={{
				backgroundImage: 'url("https://cdn.prod.website-files.com/650dc6e8c3edc32394341ca0/667e7109654405b4e76c8a47_fdghj.png")',
			}}
		>
			<Container className="flex flex-col bg-white rounded-2xl shadow-lg w-[90%] md:w-[70%] lg:w-[50%] mx-auto mt-24 p-8 space-y-4">
				<h1 className="text-2xl font-semibold text-gray-800">Privacy Management Portal</h1>
				<p className="text-gray-600 text-lg leading-relaxed">Manage your privacy effortlessly! Access our Privacy Management Portal to create, verify, and track your consent as per regulatory requirements, and exercise your data rights anytime.</p>
			</Container>

			<Container className="flex flex-col bg-white rounded-xl gap-4 w-[80%] mx-auto mt-24 p-6">
				<h1 className="text-2xl font-semibold text-gray-800">Verify Consent</h1>

				<div className="flex flex-row gap-4 w-full">
					<TextField
						label="Consent Reference ID"
						// error={Boolean(inputErrorContext.referenceId)}
						helperText={inputErrorContext.referenceId}
						onChange={(e) => setReferenceId(e.target.value)}
						value={referenceId}
						fullWidth
						required
					/>
					<TextField
						label="Consent Unique Signature"
						// error={Boolean(inputErrorContext.signature)}
						helperText={inputErrorContext.signature}
						onChange={(e) => setConsentSignature(e.target.value)}
						value={consentSignature}
						fullWidth
						required
					/>
				</div>

				<div className="mt-4 p-4 bg-gray-100 rounded-2xl w-full shadow-lg">
					<Typography variant="subtitle1">Verification Result</Typography>
					{verifyResponse ? (
						<>
							{verifyResponse.is_verified ? (
								<div className="flex items-center gap-2 text-green-600">
									<FaCheckCircle size={18} />
									<span>Verification Successful</span>
								</div>
							) : (
								<div className="flex items-center gap-2 text-red-600">
									<FaPlusCircle
										size={18}
										className="rotate-45"
									/>
									<span>Verification Failed</span>
								</div>
							)}
							{verifyResponse.purposes?.length > 0 && verifyResponse.is_verified && (
								<div className="mt-2">
									<Typography variant="subtitle2">Purposes Consented:</Typography>
									<ul className="list-disc list-inside text-sm">
										{verifyResponse.purposes.map((p, index) => (
											<li key={index}>{p}</li>
										))}
									</ul>
								</div>
							)}
						</>
					) : (
						<Typography
							variant="body2"
							className="text-gray-600"
						>
							Kindly enter the required details and click on verify button.
						</Typography>
					)}
				</div>

				<div className="flex flex-col gap-4 w-full items-center">
					<Button
						variant="contained"
						onClick={verifyConsent}
						disabled={!isValid || submitting}
						className="mt-4 w-[10rem]"
					>
						{submitting ? <CircularProgress size={24} /> : 'Verify'}
					</Button>
				</div>
				<Snackbar
					open={snackbarOpen}
					autoHideDuration={6000}
					onClose={handleCloseSnackbar}
				>
					<Alert
						onClose={handleCloseSnackbar}
						severity={snackbarSeverity}
						sx={{ width: '100%' }}
					>
						{snackbarMessage}
					</Alert>
				</Snackbar>
			</Container>
		</section>
	);
}

export default VerifyConsent;
